import { Pencil } from "lucide-react"
import { Header } from "../Components/Header/Header"
import { ColumnDef, DataTable } from "../Components/Table/DataTable"
import useGetCustomData from "../Hooks/useGetCustomData"
import useGetData from "../Hooks/useGetData"
import { AddButton, CloseButton, EditButton, ModalHeader, PageContainer, TrackIconWrapper } from "../Styles/AppStyles"
import { GpsDeviceCompany, Kit, USER_ROLE } from "../Types"
import { useEffect, useState } from "react"
import { LocationSelect } from "../Components/Select/LocationSelect"
import apiClient from "../Services/ApiClient"
import { useSnackbar } from "../Context/SnackbarProvider"
import { decode } from "../Services/DecodeToken"
import { Box, Chip, Modal, Typography } from "@mui/material"
import { Asset } from "./AssetPage"
import { FleetForm, FleetFormInput } from "./Fleet/FleetForm"
import { COLORS } from "../Styles/Colors"
import { Link } from "react-router-dom"
import { IoIosPin } from "react-icons/io"

interface Fleet {
  id: number;
  description: string;
  license_plate: string;
  gps_device_id: string;
  created_at: string;
  updated_at: string;
}

interface FleetAssignment {
  id: number;
  trailer_id: number;
  OVID: number;
  created_at: string;
  trailer_description?: string;
  license_plate?: string;
  gps_device_id?: string;
  vending_location_name?: string;
}

const TypedIoIosPin = IoIosPin as unknown as React.FC<{ size?: number | string }>;


export const FleetPage = () => {
    const [fleetAssignments, loading, error, refetch] = useGetData<FleetAssignment>('/api/fleet');

    const [fleetToEdit, setTrailerToEdit] = useState<any | null>(null);
    const [showAddModal, setShowAddModal] = useState(false);

    const handleCloseModal = () => {
        setShowAddModal(false);
        setTrailerToEdit(null);
    };

    const [ovs] = useGetCustomData('/api/location/user/ovs');

    const [assets, loadingAssets, errorAssets, refetchAssets] = useGetCustomData('/api/assets');


    const [kits, loadingKit, errorKit, refetchKit] = useGetData<Kit>('/api/kits');
    const [devices, loadingDevices, errorDevices, refetchDevices] = useGetData<GpsDeviceCompany>(`/api/gps/devices/assets/company`);
    



    const [selectedOvs, setSelectedOvs] = useState<string>('');

    const token: any = decode();

    const [filteredData, setFilteredData] = useState<FleetAssignment[]>();

    const { showSnackbar } = useSnackbar();




    useEffect(() => {
        if (token.roles.id !== USER_ROLE.ADMIN_ABATIX && ovs.length > 0 && !selectedOvs) {
            setSelectedOvs(ovs[0].OVID);
        }
    }, [token.roles.id, ovs, selectedOvs]);

    useEffect(() => {
        const filtered = selectedOvs
            ? fleetAssignments.filter(assignment => assignment.OVID === Number(selectedOvs))
            : fleetAssignments;

        setFilteredData(filtered);
    }, [fleetAssignments, selectedOvs]);

    const handleSubmit = async (fleet: FleetFormInput) => {
        const payload = {
            description: fleet.description,
            license_plate: fleet.license_plate,
            gps_device_id: fleet.gps_device_id,
        };

        console.log(fleet);


        try {
            if (fleetToEdit) {
                const response = await apiClient(`/api/fleet/${fleetToEdit.id}`, {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(fleet),
                });

                if (!response.ok) {
                    showSnackbar('Failed to save fleet', 'error');
                    return;
                }
            } else {
                const response = await apiClient('/api/fleet', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(fleet),
                });

                if (!response.ok) {
                    showSnackbar('Failed to save fleet', 'error');
                    return;
                }
            }
            showSnackbar('Fleet saved successfully', 'success');
            refetch();
            handleCloseModal();
        } catch (error) {
            console.error('Error submitting fleet:', error);
            showSnackbar('Failed to save fleet. Please try again.', 'error');
        }
    };

    const trailerColumns: ColumnDef<any>[] = [
  {
    field: 'actions',
    headerName: '',
    width: 100,
    sortable: false,
    filterable: false,
    renderCell: (params) => (
      <div
        style={{
          display: 'flex',
          gap: 8,
          alignItems: 'center',
          justifyContent: 'center',
          marginTop: 12,
        }}
      >
        <EditButton
          onClick={() => {
            setTrailerToEdit(params.row);
            setShowAddModal(true);
          }}
        >
          <Pencil />
        </EditButton>
      </div>
    ),
  },
{
  field: 'track',
  headerName: 'Track',
  width: 100,
  sortable: false,
  filterable: false,
  renderCell: (params) => {
    const deviceId = params.row.gps_device_id;

    const matchedDevice = devices.find(device => device.device_id === deviceId);

    if (!deviceId || !matchedDevice) return null;

    return (
      <Link
        to={`/maps?deviceId=${deviceId}`}
        title="View GPS Tracking"
        style={{
          display: 'flex',
          width: '100%',
          height: '100%',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <TrackIconWrapper>
          <TypedIoIosPin />
        </TrackIconWrapper>
      </Link>
    );
  },
},

  { field: 'name', headerName: 'Name', width: 250, searchable: true },
  { field: 'description', headerName: 'Description', width: 250, searchable: true },
  { field: 'license_plate', headerName: 'License Plate', width: 150, searchable: true },
  { field: 'gps_device_id', headerName: 'GPS Device ID', width: 150 },
  {
    field: 'assets',
    headerName: 'Assets',
    width: 300,
    renderCell: (params) => (
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 0.5,
          width: '100%',
          height: '100%',
        }}
      >
        {(params.row.assets || []).map((asset: any) => (
          <Chip
            key={asset.id}
            label={asset.name}
            size="small"
            sx={{ backgroundColor: COLORS.teal, color: '#fff' }}
          />
        ))}
      </Box>
    ),
  },
  {
    field: 'kits',
    headerName: 'Kits',
    width: 300,
    renderCell: (params) => (
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 0.5,
          width: '100%',
          height: '100%',
        }}
      >
        {(params.row.kits || []).map((kit: any) => (
          <Chip
            key={kit.id}
            label={kit.name}
            size="small"
            sx={{ backgroundColor: COLORS.teal, color: '#fff' }}
          />
        ))}
      </Box>
    ),
  },
  { field: 'vending_location_name', headerName: 'Assigned Location', width: 200, searchable: true },
  { field: 'created_at', headerName: 'Created', width: 180 },
];


    return (
        <PageContainer>
            <Header title="Fleet Management" data={filteredData}>
                <AddButton onClick={() => {
                    setTrailerToEdit(null);
                    setShowAddModal(true);
                }}>+</AddButton>
            </Header>

            {showAddModal && (
                <Modal open={showAddModal} onClose={() => setShowAddModal(false)}>
  <Box
    sx={{
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      backgroundColor: 'white',
      borderRadius: 2,
      maxWidth: 800,
      width: 'calc(100% - 32px)',
      maxHeight: '90dvh', 
      overflow: 'auto',  
      p: 3,
      boxShadow: 24,
    }}
    onClick={(e) => e.stopPropagation()}
  >
    <CloseButton onClick={() => setShowAddModal(false)}>x</CloseButton>

    <Typography variant="h6" gutterBottom>
      <ModalHeader>{fleetToEdit ? "Edit" : "Add"} Fleet</ModalHeader>
    </Typography>

    <FleetForm 
      assets={assets.data || []}
      kits={kits}
      ovs={ovs} 
      onSubmit={handleSubmit} 
      defaultValues={fleetToEdit ?? undefined} 
      onCancel={handleCloseModal} 
    />
  </Box>
</Modal>

            )}

            <DataTable
                columns={trailerColumns}
                data={filteredData ?? []}
                loading={loading}
                pageKey="fleet"
                idField="id"
            >
                <LocationSelect
                    ovs={ovs}
                    selectedOvs={selectedOvs}
                    onChange={setSelectedOvs}
                />
            </DataTable>
        </PageContainer>
    )
}